import {m as motion, AnimatePresence} from 'framer-motion'
import {graphql, useStaticQuery} from 'gatsby'
import React, {useState} from 'react'
import {useInView} from 'react-intersection-observer'

import {topNavigationSignupActioned, TopNavigationSignupActionedAction} from '@/analytics/segment'
import ExternalLink from '@/components/external-link'
import Image from '@/components/image'
import LocalizedLink from '@/components/localized-link'
import PrismicLink from '@/components/prismic-link'
import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import type {Props as SignupProps} from '@/components/signup-modal'
import {Text} from '@/components/text'
import type {HeaderQuery} from '@/graphql-types'
import useHasMounted from '@/hooks/use-has-mounted'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import useMediaQuery from '@/hooks/use-media-query'
import {useUser} from '@/hooks/use-user'
import logoAngled from '@/images/icons/logo-angled.svg'
import logoHorizontal from '@/images/icons/logo-horizontal.svg'
import menuCloseIcon from '@/images/icons/menu-close.svg'
import menuOpenIcon from '@/images/icons/menu-open.svg'
import {useGlobalStrings} from '@/providers/global-strings'
import {useLocale} from '@/providers/locale'
import {usePageInfo} from '@/providers/page-info'
import {useSignupModal} from '@/providers/signup-modal'
import {tokens, breakpoints, headerSizes} from '@/theme'
import type {ImageSource} from '@/types/image'
import type {PrismicData} from '@/types/prismic-data'
import {durationStringToNumber} from '@/utils/duration-string-to-number'

import * as s from './header.styles'

const AVAILABILITY_TEST_ID = 'availability-test'

export interface Props {
    hideLeftNav?: boolean
    hideRightNav?: boolean
    ctaDestination?: 'signupform' | 'demopage' | 'partnersform' | 'accountingpartners'
    exploreProductDestination?: 'explorepartnerportal' | 'exploreproduct'
    genericBookADemo?: boolean
}

type Content = PrismicData<HeaderQuery, 'allPrismicHeader'>['data']
type ContentDropdowns = Content['dropdowns']

interface NestedDropdownColumnProps {
    title: string
    icon: ImageSource
    linkItems: {
        title: string
        link: PrismicLinkProps
    }[]
    hasOneColumn: boolean
}

const NestedDropdownColumn = ({
    title,
    icon,
    linkItems,
    hasOneColumn,
}: NestedDropdownColumnProps) => {
    const [isOpen, setIsOpen] = useState(false || hasOneColumn)

    return (
        <s.NestedDropdownColumn $isExpanded={isOpen}>
            <s.ColumnHeader $isOnlyColumn={hasOneColumn}>
                <Image image={icon} />
                <s.NestedDropdownCategoryTitle onClick={() => setIsOpen(!isOpen)}>
                    <s.ColumnHeaderTitle>{title}</s.ColumnHeaderTitle>
                    <s.Arrow $isExpanded={isOpen} aria-hidden="true" />
                </s.NestedDropdownCategoryTitle>
            </s.ColumnHeader>

            <s.NestedDropdown $hasOneColumn={hasOneColumn} $isExpanded={isOpen}>
                {linkItems?.map((item, i) => {
                    return (
                        <s.NavLinkWithHover
                            key={`${item.title}-${i}`}
                            as={PrismicLink}
                            {...item.link}
                        >
                            {item.title}
                        </s.NavLinkWithHover>
                    )
                })}
            </s.NestedDropdown>
        </s.NestedDropdownColumn>
    )
}

type UniformDropdownProps = ContentDropdowns[number]['dropdown']['document']['data'] & {
    showAd: boolean
}

const UniformDropdown = ({columns, cta_column, showAd}: UniformDropdownProps) => {
    const hasOneColumn = columns.length === 1

    return (
        <s.DropdownWrapper>
            <s.DropdownSingle>
                <s.DropdownColumnsWrapper>
                    {columns.slice(0, 3).map((column) => {
                        return (
                            <div key={column.column.document.id}>
                                <NestedDropdownColumn
                                    title={column.column.document.data.label}
                                    icon={column.column.document.data.icon}
                                    linkItems={column.column.document.data.links.map((item) => ({
                                        title: item.link_text,
                                        link: item.link,
                                    }))}
                                    hasOneColumn={hasOneColumn}
                                />
                            </div>
                        )
                    })}
                    {showAd && cta_column.document?.data && (
                        <s.DropdownAd
                            $large={hasOneColumn}
                            $bgColor={
                                s.isAllowedBgColor(cta_column.document.data.background_color)
                                    ? cta_column.document.data.background_color
                                    : 'pink'
                            }
                        >
                            <Image image={cta_column.document.data.image} />
                            <s.DropdownAdBodyWrapper>
                                <Text $variant="paragraph-large">
                                    {cta_column.document.data.body}
                                </Text>
                                <s.NavCta as={PrismicLink} {...cta_column.document.data.cta_link}>
                                    {cta_column.document.data.cta_text}
                                </s.NavCta>
                            </s.DropdownAdBodyWrapper>
                        </s.DropdownAd>
                    )}
                </s.DropdownColumnsWrapper>
            </s.DropdownSingle>
        </s.DropdownWrapper>
    )
}

interface HeaderProps extends Props {
    content: Content
}

const transition = {
    type: 'tween',
    duration: durationStringToNumber(tokens.smooth) / 1000,
}

const animationOverlayProps = {
    initial: {opacity: 0},
    animate: {opacity: 1},
    exit: {opacity: 0},
    transition,
}

const animationDropdownProps = {
    initial: {
        opacity: 0,
        height: 0,
    },
    animate: {
        opacity: 1,
        height: 'auto',
        transition,
    },
    exit: {
        opacity: 0,
        height: 0,
        transition: {
            ...transition,
            delay: 0.1,
        },
    },
}

export const Header = ({
    content,
    hideLeftNav,
    hideRightNav,
    ctaDestination = 'signupform',
    exploreProductDestination = 'exploreproduct',
    genericBookADemo = false,
}: HeaderProps) => {
    const [selectedDropdownIndex, setSelectedDropdownIndex] = useState<number | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const isXSmallScreen = useMediaQuery.max(s.mobileMenuBreakpoint)
    const isSmallScreen = useMediaQuery.max(s.tabletMenuBreakpoint)
    const [mobileCtaRef, inView] = useInView({initialInView: true, skip: !isXSmallScreen})
    const showStickyCta = !inView && !menuOpen
    const [headerScrollRef, headerInView] = useInView({initialInView: true, skip: isSmallScreen})
    const showExploreProduct = menuOpen || !headerInView
    const showLogIn = menuOpen || headerInView
    const {home, menu} = useGlobalStrings()
    const {location} = usePageInfo()

    const hasMounted = useHasMounted()
    const {isLoggedIn} = useUser()
    const currentLocale = useLocale()

    const toggleMenu = () => {
        setSelectedDropdownIndex(null)
        setMenuOpen((isOpen) => !isOpen)
    }

    const handleTouchOrClick = (dropdownIndex: number) => {
        setSelectedDropdownIndex(selectedDropdownIndex !== dropdownIndex ? dropdownIndex : null)
    }

    const handleHover = (dropdownIndex: number | null) => {
        if (window.innerWidth >= breakpoints[s.laptopMenuBreakpoint]) {
            setSelectedDropdownIndex(dropdownIndex)
        }
    }

    const {renderSignupModal} = useSignupModal()

    const onSignupCtaClick = (signupProps?: SignupProps) => {
        setMenuOpen(false)
        renderSignupModal(signupProps)

        setSelectedDropdownIndex(null)
        topNavigationSignupActioned({action: TopNavigationSignupActionedAction.Started})
    }

    const getExploreProductDestination = (destination: Props['exploreProductDestination']) => {
        switch (destination) {
            case 'explorepartnerportal': {
                return (
                    <s.NavLink
                        as={LocalizedLink}
                        to="/explore-partner-portal"
                        state={{prevPath: location?.pathname}}
                    >
                        {content.explore_partner_portal}
                    </s.NavLink>
                )
            }

            case 'exploreproduct': {
                return (
                    <s.NavLink
                        as={LocalizedLink}
                        to="/explore-product"
                        state={{prevPath: location?.pathname}}
                    >
                        <s.VisibleInSmAndXl>{content.explore_product}</s.VisibleInSmAndXl>
                        <s.VisibleInMdAndLg aria-hidden>{content.explore}</s.VisibleInMdAndLg>
                    </s.NavLink>
                )
            }

            default: {
                throw new Error('Unrecognized destination')
            }
        }
    }

    const getCtaButton = (ctaDestination: Props['ctaDestination']) => {
        if (currentLocale.code === 'en-us') {
            return (
                <s.NavCta
                    as={LocalizedLink}
                    to="/getting-started"
                    state={{prevPath: location?.pathname}}
                >
                    {content.start_free_trial}
                </s.NavCta>
            )
        }

        switch (ctaDestination) {
            case 'signupform': {
                return (
                    <s.NavCta onClick={() => onSignupCtaClick()}>
                        {content.start_free_trial}
                    </s.NavCta>
                )
            }

            case 'accountingpartners': {
                return (
                    <s.NavCta
                        onClick={() =>
                            onSignupCtaClick({
                                signupProps: {
                                    form: 'Partner',
                                    isPartner: true,
                                },
                            })
                        }
                    >
                        {content.start_free_trial}
                    </s.NavCta>
                )
            }

            case 'demopage': {
                return (
                    <s.NavCta as={LocalizedLink} to="/demo">
                        {content.start_free_trial}
                    </s.NavCta>
                )
            }

            case 'partnersform': {
                return (
                    <s.NavCta
                        as={LocalizedLink}
                        to="/bigform-partner"
                        onClick={() =>
                            topNavigationSignupActioned({
                                action: TopNavigationSignupActionedAction.Partners,
                            })
                        }
                    >
                        {content.start_free_trial}
                    </s.NavCta>
                )
            }

            default: {
                throw new Error('Unrecognized destination')
            }
        }
    }

    return (
        <>
            <AnimatePresence>
                {(selectedDropdownIndex !== null || menuOpen) && (
                    <s.Overlay
                        {...animationOverlayProps}
                        $mobileMenuOpen={menuOpen}
                        $dropdownOpen={selectedDropdownIndex !== null}
                    />
                )}
            </AnimatePresence>

            <s.ScrollTrigger ref={headerScrollRef} $threshold={`${headerSizes.heightLarge}px`} />

            <s.HeaderWrapper
                data-testid={AVAILABILITY_TEST_ID}
                $menuOpen={menuOpen}
                $dropdownOpen={selectedDropdownIndex !== null}
            >
                <s.HeaderInner $menuOpen={menuOpen}>
                    <s.DesktopLogo>
                        <LocalizedLink to="/">
                            <img src={logoAngled} alt={home} />
                        </LocalizedLink>
                    </s.DesktopLogo>
                    {showStickyCta ? (
                        <s.StickyCtaWrapper>{getCtaButton(ctaDestination)}</s.StickyCtaWrapper>
                    ) : (
                        <s.MobileLogo>
                            <LocalizedLink to="/">
                                <img src={logoHorizontal} alt={home} />
                            </LocalizedLink>
                        </s.MobileLogo>
                    )}

                    {(!hideLeftNav || !hideRightNav) && (
                        <>
                            <s.Nav>
                                {!hideLeftNav && (
                                    <s.NavGroupLeft>
                                        {content.dropdowns.map(
                                            ({label, link, show_badge, dropdown}, index) => {
                                                if (index > 4) {
                                                    return null
                                                }

                                                const data = dropdown.document?.data
                                                const isActive = selectedDropdownIndex === index
                                                const isAdVisible =
                                                    data?.columns.length < 3 && !isSmallScreen

                                                if (data) {
                                                    return (
                                                        <s.NavMenu
                                                            onMouseEnter={() => handleHover(index)}
                                                            onMouseLeave={() => handleHover(null)}
                                                            key={`header-dropdown-${label}-${index}`}
                                                        >
                                                            <s.MenuBtnWithDropdown
                                                                aria-expanded={isActive}
                                                                aria-controls={`header-dropdown-${index}`}
                                                                onClick={() =>
                                                                    handleTouchOrClick(index)
                                                                }
                                                            >
                                                                <s.NavLink
                                                                    as="span"
                                                                    $isHighlighted={isActive}
                                                                >
                                                                    {label}

                                                                    <s.Arrow
                                                                        $isExpanded={isActive}
                                                                        aria-hidden="true"
                                                                    />
                                                                </s.NavLink>
                                                            </s.MenuBtnWithDropdown>

                                                            <div
                                                                id={`header-dropdown-${index}`}
                                                                tabIndex={-1}
                                                            >
                                                                <AnimatePresence initial={false}>
                                                                    {isActive && (
                                                                        <motion.div
                                                                            {...animationDropdownProps}
                                                                        >
                                                                            <UniformDropdown
                                                                                showAd={isAdVisible}
                                                                                {...data}
                                                                            />
                                                                        </motion.div>
                                                                    )}
                                                                </AnimatePresence>
                                                            </div>
                                                        </s.NavMenu>
                                                    )
                                                }

                                                if (link) {
                                                    return (
                                                        <s.NavLink
                                                            key={`header-dropdown-${index}`}
                                                            as={PrismicLink}
                                                            $isDirectLink
                                                            {...link}
                                                        >
                                                            {label}

                                                            {show_badge && (
                                                                <s.MenuBadge aria-hidden="true" />
                                                            )}
                                                        </s.NavLink>
                                                    )
                                                }

                                                return null
                                            },
                                        )}
                                    </s.NavGroupLeft>
                                )}

                                {!hideRightNav && (
                                    <s.NavGroupRight>
                                        {!hasMounted ? null : isLoggedIn ? (
                                            <s.CtaWrapper $menuOpen={menuOpen}>
                                                <s.NavCta
                                                    as={ExternalLink}
                                                    to={process.env.GATSBY_PLEO_APP_URL}
                                                >
                                                    {content.app_link_text}
                                                </s.NavCta>
                                            </s.CtaWrapper>
                                        ) : (
                                            <>
                                                {!!content.explore &&
                                                    showExploreProduct &&
                                                    getExploreProductDestination(
                                                        exploreProductDestination,
                                                    )}
                                                {showLogIn && (
                                                    <s.NavLink
                                                        as={ExternalLink}
                                                        to={`${process.env.GATSBY_PLEO_APP_URL}/login?lang=${currentLocale.language}`}
                                                    >
                                                        {content.log_in}
                                                    </s.NavLink>
                                                )}

                                                {genericBookADemo ? (
                                                    <s.NavLink
                                                        as={LocalizedLink}
                                                        rawPath
                                                        to={`/ie/demo?country=${currentLocale.country}`}
                                                    >
                                                        {content.book_a_demo}
                                                    </s.NavLink>
                                                ) : (
                                                    <s.NavLink as={LocalizedLink} to="/demo">
                                                        {content.book_a_demo}
                                                    </s.NavLink>
                                                )}

                                                <s.CtaWrapper $menuOpen={menuOpen}>
                                                    {getCtaButton(ctaDestination)}
                                                </s.CtaWrapper>
                                            </>
                                        )}
                                    </s.NavGroupRight>
                                )}
                            </s.Nav>

                            {(!hideLeftNav || isSmallScreen) && (
                                <s.MobileMenuBtn onClick={toggleMenu}>
                                    <img src={menuOpen ? menuCloseIcon : menuOpenIcon} alt={menu} />
                                </s.MobileMenuBtn>
                            )}
                        </>
                    )}
                </s.HeaderInner>
            </s.HeaderWrapper>

            <s.ScrollTrigger ref={mobileCtaRef} $threshold="330px" />
        </>
    )
}

const HeaderContainer = (props: Props) => {
    const data = useStaticQuery<HeaderQuery>(query)
    const [content] = useLocalizedContent(data.allPrismicHeader.nodes)

    return <Header content={content.data} {...props} />
}

const query = graphql`
    query Header {
        allPrismicHeader {
            nodes {
                lang
                data {
                    start_free_trial
                    book_a_demo
                    log_in
                    explore
                    explore_product
                    explore_partner_portal
                    app_link_text
                    dropdowns {
                        label
                        link {
                            ...PrismicLink
                        }
                        show_badge
                        dropdown {
                            document {
                                ... on PrismicHeaderDropdown {
                                    data {
                                        columns {
                                            column {
                                                document {
                                                    ... on PrismicHeaderDropdownColumn {
                                                        id
                                                        data {
                                                            icon {
                                                                alt
                                                                url
                                                                copyright
                                                                dimensions {
                                                                    height
                                                                    width
                                                                }
                                                            }
                                                            label
                                                            links {
                                                                link_text
                                                                link {
                                                                    ...PrismicLink
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        cta_column {
                                            document {
                                                ... on PrismicHeaderDropdownCta {
                                                    data {
                                                        background_color
                                                        body
                                                        cta_text
                                                        cta_link {
                                                            ...PrismicLink
                                                        }
                                                        image {
                                                            alt
                                                            url
                                                            copyright
                                                            dimensions {
                                                                height
                                                                width
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default HeaderContainer
